import { token } from '@bp-files/ui';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  FRAME_BUTTON_CLASS,
  FRAME_TOUCH_BUTTON_CLASS,
} from '@/components/Viewer/ViewerFrame';
import { PDF_VIEWER_COPY } from './pdfViewer.copy';
import type { PdfControls } from './pdfControls';

/**
 * `‹ [3] of 6 ›` — where the reader is, and the two ways to move one step.
 *
 * One implementation, rendered by whichever bar the viewport gave the job to:
 * the desktop toolbar or the mobile bottom bar. Never both at once — a second
 * copy in the DOM would be a second control answering to "Previous page", and
 * every query in this component's tests names controls by role and label.
 *
 * **The input stays, beside the arrows rather than instead of them.** Typing
 * `47` is the only way to cross a long filing in one act, and it is what the
 * browser's own reader offers; the arrows are what a thumb can hit. The two are
 * not redundant, they are the same act at two costs.
 */
export function PdfPager({
  controls,
  touch,
}: {
  controls: PdfControls;
  /** Thumb-sized arrows rather than cursor-sized. The mobile bottom bar's. */
  touch?: boolean;
}) {
  const { titleId, pageCount, currentPage, goToPage } = controls;
  const buttonClass = touch === true ? FRAME_TOUCH_BUTTON_CLASS : FRAME_BUTTON_CLASS;

  return (
    <span className="flex items-center gap-1">
      <button
        type="button"
        disabled={currentPage <= 1}
        onClick={() => {
          goToPage(currentPage - 1);
        }}
        aria-label={PDF_VIEWER_COPY.page.previous}
        className={`${buttonClass} text-secondary hover:text-body`}
      >
        <ChevronLeftIcon />
      </button>

      {/*
        The label is visually hidden rather than absent: the input needs an
        accessible name, and "Page" printed beside an arrow, a number and "of 6"
        is a word the sighted reader has already been told by the arrows.
      */}
      <label htmlFor={`${titleId}-page`} className="sr-only">
        {PDF_VIEWER_COPY.page.label}
      </label>
      <input
        id={`${titleId}-page`}
        type="number"
        min={1}
        max={pageCount}
        value={currentPage}
        onChange={(event) => {
          goToPage(Number(event.target.value));
        }}
        className="w-12 rounded-md border px-1 py-1 text-center text-xs tabular-nums"
        style={{ borderColor: token('bp-border'), background: token('bp-app') }}
      />

      <span
        aria-live="polite"
        className="text-xs tabular-nums"
        style={{ color: token('bp-text-secondary') }}
      >
        {PDF_VIEWER_COPY.page.of(pageCount)}
      </span>

      <button
        type="button"
        disabled={currentPage >= pageCount}
        onClick={() => {
          goToPage(currentPage + 1);
        }}
        aria-label={PDF_VIEWER_COPY.page.next}
        className={`${buttonClass} text-secondary hover:text-body`}
      >
        <ChevronRightIcon />
      </button>
    </span>
  );
}
